/**
 * Recruitment ATS — error handling.
 *
 * The backend error envelope is `{ code, message, errorCode? }`. The shared
 * axios layer (src/services/api.ts) rejects with a transformed object
 * `{ message, status, code, details }` where `details` is that raw envelope.
 * We normalize both shapes here and — per rule — branch on `errorCode`
 * (only `duplicate_application` in Phase 1), never on message text.
 */

export interface RecruitmentError {
	status?: number;
	message: string;
	errorCode?: string;
}

export function getRecruitmentError(err: unknown): RecruitmentError {
	const e = err as {
		status?: number;
		message?: string;
		details?: { code?: number; message?: string; errorCode?: string };
		response?: { status?: number; data?: { message?: string; errorCode?: string } };
	};

	const raw = e?.details ?? e?.response?.data;
	return {
		status: e?.status ?? e?.response?.status,
		message: raw?.message || e?.message || 'Something went wrong. Please try again.',
		errorCode: (raw as { errorCode?: string })?.errorCode,
	};
}

/** True when a person already has an application on that opening (409). */
export function isDuplicateApplication(err: unknown): boolean {
	return getRecruitmentError(err).errorCode === 'duplicate_application';
}
